import { useState, type ReactNode } from "react";

import { attachmentUrl } from "../lib/api.js";
import type { MailAddress, MessageDetail } from "../lib/types.js";
import { HtmlFrame } from "./HtmlFrame.js";

interface MessageViewProps {
  message: MessageDetail | null;
  loading: boolean;
  error: string | null;
}

const SIZE_UNITS = ["kB", "MB", "GB"];

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;

  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < SIZE_UNITS.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = value.toLocaleString("nl-NL", { maximumFractionDigits: value < 10 ? 1 : 0 });
  return `${rounded} ${SIZE_UNITS[unit] ?? "kB"}`;
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("nl-NL", {
    weekday: "short",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatAddress(person: MailAddress): string {
  const name = person.name.trim();
  if (name === "") return person.address;
  return person.address === "" ? name : `${name} <${person.address}>`;
}

function AddressRow({ label, people }: { label: string; people: MailAddress[] }): ReactNode {
  if (people.length === 0) return null;
  return (
    <div className="detail-row">
      <span className="detail-label">{label}</span>
      <span className="detail-value">{people.map(formatAddress).join(", ")}</span>
    </div>
  );
}

function Skeleton(): ReactNode {
  return (
    <div className="detail-skeleton" aria-hidden="true">
      <span className="skeleton skeleton--title" />
      <span className="skeleton skeleton--text" />
      <span className="skeleton skeleton--text" />
      <span className="skeleton skeleton--block" />
    </div>
  );
}

export function MessageView({ message, loading, error }: MessageViewProps): ReactNode {
  /**
   * De keuze om externe afbeeldingen te laden geldt per bericht en verdwijnt
   * zodra je een ander bericht opent — de key op deze component in Mail.tsx
   * zorgt daarvoor.
   */
  const [showImages, setShowImages] = useState(false);

  if (error !== null) return <p className="pane-message pane-message--error">{error}</p>;
  if (message === null) {
    return loading ? <Skeleton /> : <p className="pane-message">Selecteer een bericht om het te lezen.</p>;
  }

  const attachments = message.attachments.filter((attachment) => !attachment.inline);

  return (
    <article className="detail">
      <header className="detail-head">
        <h1 className="detail-subject">
          {message.subject.trim() === "" ? "(geen onderwerp)" : message.subject}
        </h1>
        <AddressRow label="Van" people={message.from} />
        <AddressRow label="Aan" people={message.to} />
        <AddressRow label="Cc" people={message.cc} />
        <div className="detail-row">
          <span className="detail-label">Datum</span>
          <time className="detail-value" dateTime={message.date}>
            {formatDate(message.date)}
          </time>
        </div>
      </header>

      {attachments.length > 0 && (
        <section className="attachments" aria-label="Bijlagen">
          <h2 className="attachments-title">
            {attachments.length === 1 ? "1 bijlage" : `${attachments.length} bijlagen`}
          </h2>
          <ul className="attachment-list">
            {attachments.map((attachment) => (
              <li key={attachment.partId}>
                <a
                  className="attachment"
                  href={attachmentUrl(message.folder, message.uid, attachment.partId)}
                  // De server dwingt Content-Disposition: attachment af; download
                  // geeft de browser bovendien de juiste bestandsnaam.
                  download={attachment.filename}
                >
                  <span className="attachment-name">{attachment.filename}</span>
                  <span className="attachment-size">{formatSize(attachment.size)}</span>
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}

      {message.hasBlockedImages && !showImages && (
        <div className="notice">
          <p className="notice-text">
            Afbeeldingen zijn geblokkeerd om te voorkomen dat de afzender ziet dat u dit bericht
            opent.
          </p>
          <button type="button" className="button button--small" onClick={() => setShowImages(true)}>
            Afbeeldingen tonen
          </button>
        </div>
      )}

      {message.hasHtml ? (
        <HtmlFrame folder={message.folder} uid={message.uid} showImages={showImages} />
      ) : (
        <pre className="detail-text">{message.text}</pre>
      )}
    </article>
  );
}
